import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { Theme } from "@mui/material";
import { CompactPalette, StandardPalette } from "@Themes/palettes/mui-palette-extension";
import dayjs from "dayjs";

const MIN_MONTHS_FOR_OLD_ACCOUNT = 2;

interface IPerformanceInterval {
  EXCELLENT: number;
  STRONG: number;
  FAIR: number;
  POOR: number;
}

export const PerformanceIntervals: Record<string, IPerformanceInterval> = {
  BYB_IQ: {
    EXCELLENT: 600,
    STRONG: 330,
    FAIR: 180,
    POOR: 0,
  },
  SCAN_FREQUENCY: {
    EXCELLENT: 0.6,
    STRONG: 0.3,
    FAIR: 0.15,
    POOR: 0,
  },
  SCAN_TIMING: {
    EXCELLENT: 75,
    STRONG: 55,
    FAIR: 35,
    POOR: 0,
  },
  AWARENESS: {
    EXCELLENT: 75,
    STRONG: 55,
    FAIR: 35,
    POOR: 0,
  },
  CRITICAL_SCAN: {
    EXCELLENT: 0.5,
    STRONG: 0.3,
    FAIR: 0.15,
    POOR: 0,
  },
  DECISION: {
    EXCELLENT: 75,
    STRONG: 55,
    FAIR: 35,
    POOR: 0,
  },
} as const;

export enum PerformanceOutcome {
  EXCELLENT = "excellent",
  STRONG = "strong",
  FAIR = "fair",
  POOR = "poor",
  BUILDING = "building",
  LEARNING = "learning",
}

export const getPerformanceOutcomeLabel = (outcome: PerformanceOutcome): string => {
  const labels: Record<PerformanceOutcome, string> = {
    [PerformanceOutcome.EXCELLENT]: "Excellent",
    [PerformanceOutcome.STRONG]: "Strong",
    [PerformanceOutcome.FAIR]: "Fair",
    [PerformanceOutcome.POOR]: "Poor",
    [PerformanceOutcome.BUILDING]: "Building",
    [PerformanceOutcome.LEARNING]: "Learning",
  };
  return labels[outcome];
};

const PerformanceOutcomeEmojiMap = {
  [PerformanceOutcome.EXCELLENT]: ["🏆"],
  [PerformanceOutcome.STRONG]: ["🤩"],
  [PerformanceOutcome.FAIR]: ["👏"],
  [PerformanceOutcome.POOR]: ["😖"],
};

export const getPerformanceOutcome = (score: number, target: number) => {
  const scoreDelta = score / target;

  if (scoreDelta >= 2) return PerformanceOutcome.EXCELLENT;
  else if (scoreDelta >= 1.5) return PerformanceOutcome.STRONG;
  else if (scoreDelta >= 1) return PerformanceOutcome.FAIR;

  return PerformanceOutcome.POOR;
};

export const getPerformanceLabel = (metricKey: MetricFilterTypes, score: number, creationDate: number | null): string => {
  const isOldAccount = isAccountOlderThanTwoMonths(creationDate);
  const interval = PerformanceIntervals[metricKey];

  if (score >= interval.EXCELLENT) return PerformanceOutcome.EXCELLENT;
  else if (score >= interval.STRONG) return PerformanceOutcome.STRONG;
  else if (score >= interval.FAIR) return isOldAccount ? PerformanceOutcome.FAIR : PerformanceOutcome.BUILDING;
  return isOldAccount ? PerformanceOutcome.POOR : PerformanceOutcome.LEARNING;
};

export const getPerformanceProgressLabel = (diff: number) => {
  if (diff >= 10) return PerformanceOutcome.EXCELLENT;
  else if (diff >= 5) return PerformanceOutcome.STRONG;
  else if (diff >= 0) return PerformanceOutcome.FAIR;

  return PerformanceOutcome.POOR;
};

export const getPerformanceColor = (metricKey: MetricFilterTypes, score: number, theme: Theme, creationDate: number | null) => {
  return getPerformanceShade(metricKey, score, theme, creationDate)?.v200;
};

export const getPerformanceShade = (metricKey: MetricFilterTypes, score: number, theme: Theme, creationDate: number | null) => {
  const isOldAccount = isAccountOlderThanTwoMonths(creationDate);
  const interval = PerformanceIntervals[metricKey];

  if (score >= interval.EXCELLENT) return theme.palette.dsPurple || theme.palette.dsSecondaryPurple;
  else if (score >= interval.STRONG) return theme.palette.dsGreen || theme.palette.dsSuccess;
  else if (score >= interval.FAIR) return theme.palette.dsOrange || theme.palette.dsWarning;

  return isOldAccount ? theme.palette.dsRed || theme.palette.dsDestructive : theme.palette.dsYellow || theme.palette.dsSecondaryYellow;
};

export const getBybIqColor = (score: number, theme: Theme, creationDate: number | null) => {
  return getBybIqShade(score, theme, creationDate)?.v200 || theme.palette.dsSurface;
};

export const getBybIqShade = (score: number, theme: Theme, creationDate: number | null): StandardPalette | CompactPalette => {
  const isOldAccount = isAccountOlderThanTwoMonths(creationDate);

  if (score >= PerformanceIntervals.BYB_IQ.EXCELLENT) return theme.palette.dsPurple || theme.palette.dsSecondaryPurple;
  else if (score >= PerformanceIntervals.BYB_IQ.STRONG) return theme.palette.dsGreen || theme.palette.dsSuccess;
  else if (score >= PerformanceIntervals.BYB_IQ.FAIR) return theme.palette.dsOrange || theme.palette.dsWarning;

  return isOldAccount ? theme.palette.dsRed || theme.palette.dsDestructive : theme.palette.dsYellow || theme.palette.dsSecondaryYellow;
};

export const getImprovementColor = (score: number, theme: Theme) => {
  const isNeutral = score <= 0.5 && score >= -0.5;
  if (isNeutral) return theme.palette.dsNeutral?.v400;

  const shade = getImprovementShade(score, theme);
  return shade?.v200;
};

export const getImprovementShade = (score: number, theme: Theme) => {
  if (score > 0.5) return theme.palette.dsGreen;
  if (score < -0.5) return theme.palette.dsRed;

  return theme.palette.dsNeutral;
};

export const getImprovementLabel = (score: number): string => {
  if (score > 0.5) return "progress.trend.performance.great";
  if (score < -0.5) return "progress.trend.performance.refocus";
  return "progress.trend.performance.stable";
};

export const getPeformanceOutcomeDifference = (curr: number, prev: number): number => {
  return parseFloat((((curr - prev) / prev) * 100).toFixed(2));
};

export const getFeedbackTitle = (seed: number, outcome: PerformanceOutcome, isBootcamp: boolean, isSubtitle: boolean = false) => {
  const index = (seed % (isBootcamp ? 2 : 3)) + 1;
  const category = isBootcamp ? "bootcamp." : "";
  const suffix = isSubtitle ? "-subtitle" : "";
  return `session-result.outcome.${category}${String(outcome).toLowerCase()}-${index}${suffix}`;
};

export const getWeeklyProgressFeedbackTitle = (seed: number, outcome: PerformanceOutcome, isSubtitle: boolean = false) => {
  const index = (seed % 3) + 1;
  const suffix = isSubtitle ? "-subtitle" : "";
  return `weekly-progress.outcome.${String(outcome).toLowerCase()}-${index}${suffix}`;
};

export const getFeedbackSubtitle = (seed: number, outcome: PerformanceOutcome, isBootcamp: boolean) => {
  return getFeedbackTitle(seed, outcome, isBootcamp, true);
};

export const getFeedbackEmoji = (outcome: PerformanceOutcome) => {
  const emojiCategory = PerformanceOutcomeEmojiMap[outcome as keyof typeof PerformanceOutcomeEmojiMap];
  return emojiCategory[Math.floor(Math.random() * emojiCategory.length)];
};

export const isAccountOlderThanTwoMonths = (creationDate: number | null): boolean => {
  if (!creationDate) return false;

  const creationDateInSeconds = Math.floor(creationDate / 1000);
  const accountAge = dayjs().diff(dayjs.unix(creationDateInSeconds), "month");

  return accountAge >= MIN_MONTHS_FOR_OLD_ACCOUNT;
};

export const getPerformanceFeedbackMessage = (metricKey: MetricFilterTypes, score: number, isOnLearningPhase: boolean): string => {
  const accountPhase = isOnLearningPhase ? "learning" : "performance";
  const interval = PerformanceIntervals[metricKey];

  if (score >= interval.EXCELLENT) return `progress.feedback.${accountPhase}.excellent`;
  else if (score >= interval.STRONG) return `progress.feedback.${accountPhase}.strong`;
  else if (score >= interval.FAIR) return `progress.feedback.${accountPhase}.fair`;
  return `progress.feedback.${accountPhase}.poor`;
};

export const getImprovementLabelForAccount = (score: number, creationDate: number | null): string => {
  const isOldAccount = isAccountOlderThanTwoMonths(creationDate);

  if (score >= 10) return PerformanceOutcome.EXCELLENT;
  if (score >= 0) return PerformanceOutcome.STRONG;
  if (score >= -5) return isOldAccount ? PerformanceOutcome.FAIR : PerformanceOutcome.BUILDING;
  return isOldAccount ? PerformanceOutcome.POOR : PerformanceOutcome.LEARNING;
};

/*
 * Legacy code needed for metric logic on mobile
 */

export const PerformanceIntervalsLegacy: [number, number][] = [
  [0, 20],
  [20, 40],
  [40, 60],
  [60, 80],
  [80, 100],
];

export const getPerformanceColorLegacy = (score: number, theme: Theme) => {
  if (score >= 80) return theme.palette.dsSecondaryPurple.v300;
  else if (score >= 60) return theme.palette.dsPrimary.v300;
  else if (score >= 40) return theme.palette.dsSuccess.v300;
  else if (score >= 20) return theme.palette.dsWarning.v300;

  return theme.palette.dsDestructive.v300;
};

export const getPerformanceLabelLegacy = (score: number) => {
  if (score >= 80) return PerformanceOutcome.EXCELLENT;
  else if (score >= 60) return PerformanceOutcome.STRONG;
  else if (score >= 40) return PerformanceOutcome.FAIR;
  else if (score >= 20) return PerformanceOutcome.POOR;

  return PerformanceOutcome.POOR;
};
